import React, { useState, useEffect, useCallback } from 'react';
import { Briefcase, Eye, MousePointerClick, FileText, Filter, Search, RefreshCw, ExternalLink, Edit } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';

interface Vacancy {
  id: number;
  source: string;
  externalId: string;
  title: string;
  company: string;
  location: string;
  salary?: {
    from?: number;
    to?: number;
    currency?: string;
  };
  description: string;
  requirements: string[];
  experience: string;
  employment: string;
  keySkills: string[];
  url: string;
  isActive: boolean;
  parsedAt: string;
  viewsCount: number;
  clicksCount: number;
  applicationsCount: number;
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string;
  seoOgTitle?: string;
  seoOgDescription?: string;
  seoOgImage?: string;
  seoCanonical?: string;
  seoHreflang?: Array<{ lang: string; url: string }>;
  seoMetaRobots?: string;
  seoCustom?: any;
}

const ScrapedVacanciesPage: React.FC = () => {
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 1 });
  const [analytics, setAnalytics] = useState<any>(null);
  const [sourceStats, setSourceStats] = useState<any[]>([]);
  
  // Фильтры
  const [filters, setFilters] = useState({
    search: '',
    source: '',
    isActive: '',
    page: 1
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedVacancy, setSelectedVacancy] = useState<Vacancy | null>(null);
  const [showSEOModal, setShowSEOModal] = useState(false);
  const [seoData, setSeoData] = useState<any>({});
  const [analyticsData, setAnalyticsData] = useState<any>(null);

  useEffect(() => {
    loadVacancies();
    loadAnalytics();
    loadSourceStats();
  }, [filters]);

  const loadVacancies = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        page: filters.page,
        limit: 50
      };
      
      if (filters.search) params.search = filters.search;
      if (filters.source) params.source = filters.source;
      if (filters.isActive !== '') params.isActive = filters.isActive;

      const response = await api.get('/admin/scraped-vacancies', { params });
      
      if (response.data.success) {
        setVacancies(response.data.vacancies || []);
        setPagination(response.data.pagination || { total: 0, page: 1, limit: 50, totalPages: 1 });
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки вакансий');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadAnalytics = async () => {
    try {
      const response = await api.get('/admin/scraped-vacancies/analytics/overview');
      if (response.data.success) {
        setAnalytics(response.data.analytics);
      }
    } catch (error: any) {
      console.error('Ошибка загрузки аналитики:', error);
    }
  };

  const loadSourceStats = async () => {
    try {
      const response = await api.get('/admin/scraped-vacancies/analytics/sources');
      if (response.data.success) {
        setSourceStats(response.data.statistics || []);
      }
    } catch (error: any) {
      console.error('Ошибка загрузки статистики:', error);
    }
  };

  const handleToggleActive = async (id: number) => {
    try {
      const response = await api.post(`/admin/scraped-vacancies/${id}/toggle-active`);
      if (response.data.success) {
        toast.success('Статус вакансии изменен');
        loadVacancies();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка изменения статуса');
    }
  };

  const handleEditSEO = async (vacancy: Vacancy) => {
    try {
      const response = await api.get(`/admin/scraped-vacancies/${vacancy.id}`);
      if (response.data.success) {
        setSelectedVacancy(response.data.vacancy);
        setSeoData({
          seoTitle: response.data.vacancy.seoTitle || '',
          seoDescription: response.data.vacancy.seoDescription || '',
          seoKeywords: response.data.vacancy.seoKeywords || '',
          seoOgTitle: response.data.vacancy.seoOgTitle || '',
          seoOgDescription: response.data.vacancy.seoOgDescription || '',
          seoOgImage: response.data.vacancy.seoOgImage || '',
          seoCanonical: response.data.vacancy.seoCanonical || '',
          seoMetaRobots: response.data.vacancy.seoMetaRobots || ''
        });
        setAnalyticsData({
          viewsCount: response.data.vacancy.viewsCount || 0,
          clicksCount: response.data.vacancy.clicksCount || 0,
          applicationsCount: response.data.vacancy.applicationsCount || 0
        });
        setShowSEOModal(true);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки данных вакансии');
    }
  };

  const handleSaveSEO = async () => {
    if (!selectedVacancy) return;

    try {
      const response = await api.put(`/admin/scraped-vacancies/${selectedVacancy.id}/seo`, seoData);
      if (response.data.success) {
        toast.success('SEO теги обновлены');
        setShowSEOModal(false);
        loadVacancies();
        if (response.data.analytics) {
          setAnalyticsData(response.data.analytics);
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка обновления SEO тегов');
    }
  };

  const formatSalary = (salary?: { from?: number; to?: number; currency?: string }) => {
    if (!salary) return 'Не указана';
    if (salary.from && salary.to) {
      return `${salary.from.toLocaleString()} - ${salary.to.toLocaleString()} ${salary.currency || '₽'}`;
    }
    if (salary.from) {
      return `от ${salary.from.toLocaleString()} ${salary.currency || '₽'}`;
    }
    if (salary.to) {
      return `до ${salary.to.toLocaleString()} ${salary.currency || '₽'}`;
    }
    return 'Не указана';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Парсерные вакансии</h1>
          <p className="text-gray-600 mt-2">Управление вакансиями, собранными с различных площадок</p>
        </div>
      </div>

      {/* Аналитика */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Всего вакансий</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.total_vacancies || 0}</p>
              </div>
              <Briefcase className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Активных</p>
                <p className="text-2xl font-bold text-green-600">{analytics.active_vacancies || 0}</p>
              </div>
              <Briefcase className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Просмотры</p>
                <p className="text-2xl font-bold text-blue-600">{analytics.total_views || 0}</p>
              </div>
              <Eye className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Отклики</p>
                <p className="text-2xl font-bold text-purple-600">{analytics.total_applications || 0}</p>
              </div>
              <FileText className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>
      )}

      {/* Статистика по источникам */}
      {sourceStats.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Статистика по источникам</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Источник</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Всего</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Активных</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Просмотры</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Клики</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Отклики</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sourceStats.map((stat) => (
                  <tr key={stat.source}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{stat.source}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{stat.count}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{stat.active_count}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{stat.total_views}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{stat.total_clicks}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{stat.total_applications}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Фильтры */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Filter className="h-5 w-5" />
            <span>Фильтры</span>
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Поиск</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                placeholder="Название, компания..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Источник</label>
              <select
                value={filters.source}
                onChange={(e) => setFilters({ ...filters, source: e.target.value, page: 1 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Все</option>
                <option value="hh.ru">HeadHunter</option>
                <option value="avito.ru">Avito</option>
                <option value="rabota.ru">Rabota.ru</option>
                <option value="superjob.ru">SuperJob</option>
                <option value="zarplata.ru">Зарплата.ру</option>
                <option value="trudvsem.ru">ТрудВсем</option>
                <option value="job.ru">Job.ru</option>
                <option value="career.ru">Career.ru</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Статус</label>
              <select
                value={filters.isActive}
                onChange={(e) => setFilters({ ...filters, isActive: e.target.value, page: 1 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Все</option>
                <option value="true">Активные</option>
                <option value="false">Неактивные</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Таблица вакансий */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Загрузка вакансий...</p>
          </div>
        ) : vacancies.length === 0 ? (
          <div className="text-center py-12">
            <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Вакансии не найдены</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Название</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Компания</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Источник</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Зарплата</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Просмотры</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Клики</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Отклики</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дата</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vacancies.map((vacancy) => (
                    <tr key={vacancy.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                          {vacancy.title}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{vacancy.company}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                          {vacancy.source}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatSalary(vacancy.salary)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{vacancy.viewsCount}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{vacancy.clicksCount}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{vacancy.applicationsCount}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(vacancy.parsedAt).toLocaleDateString('ru-RU')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleActive(vacancy.id)}
                            className={`px-3 py-1 text-xs rounded ${
                              vacancy.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {vacancy.isActive ? 'Активна' : 'Неактивна'}
                          </button>
                          <button
                            onClick={() => handleEditSEO(vacancy)}
                            className="p-1 text-gray-600 hover:text-blue-600"
                            title="Редактировать SEO"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <a
                            href={vacancy.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 text-gray-600 hover:text-blue-600"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Пагинация */}
            {pagination.totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Показано {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} из {pagination.total}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Назад
                  </button>
                  <span className="px-4 py-2 text-gray-700">
                    Страница {pagination.page} из {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
                    disabled={pagination.page === pagination.totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Вперед
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Модальное окно редактирования SEO */}
      {showSEOModal && selectedVacancy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Редактирование SEO тегов</h2>
              <button
                onClick={() => setShowSEOModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="mb-4">
              <h3 className="font-semibold mb-2">{selectedVacancy.title}</h3>
              <p className="text-sm text-gray-600">{selectedVacancy.company} • {selectedVacancy.location}</p>
            </div>

            {analyticsData && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-2">Аналитика</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Просмотры:</span> {analyticsData.viewsCount}
                  </div>
                  <div>
                    <span className="text-gray-600">Клики:</span> {analyticsData.clicksCount}
                  </div>
                  <div>
                    <span className="text-gray-600">Отклики:</span> {analyticsData.applicationsCount}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">SEO Title (до 60 символов)</label>
                <input
                  type="text"
                  value={seoData.seoTitle || ''}
                  onChange={(e) => setSeoData({ ...seoData, seoTitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  maxLength={60}
                />
                <p className="text-xs text-gray-500 mt-1">{seoData.seoTitle?.length || 0}/60</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">SEO Description (до 160 символов)</label>
                <textarea
                  value={seoData.seoDescription || ''}
                  onChange={(e) => setSeoData({ ...seoData, seoDescription: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                  maxLength={160}
                />
                <p className="text-xs text-gray-500 mt-1">{seoData.seoDescription?.length || 0}/160</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Keywords</label>
                <input
                  type="text"
                  value={seoData.seoKeywords || ''}
                  onChange={(e) => setSeoData({ ...seoData, seoKeywords: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">OG Title</label>
                <input
                  type="text"
                  value={seoData.seoOgTitle || ''}
                  onChange={(e) => setSeoData({ ...seoData, seoOgTitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">OG Description</label>
                <textarea
                  value={seoData.seoOgDescription || ''}
                  onChange={(e) => setSeoData({ ...seoData, seoOgDescription: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">OG Image URL</label>
                <input
                  type="text"
                  value={seoData.seoOgImage || ''}
                  onChange={(e) => setSeoData({ ...seoData, seoOgImage: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Canonical URL</label>
                <input
                  type="text"
                  value={seoData.seoCanonical || ''}
                  onChange={(e) => setSeoData({ ...seoData, seoCanonical: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Meta Robots</label>
                <input
                  type="text"
                  value={seoData.seoMetaRobots || ''}
                  onChange={(e) => setSeoData({ ...seoData, seoMetaRobots: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="index, follow"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowSEOModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleSaveSEO}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrapedVacanciesPage;


